/* AppErrorBoundary — catches uncaught React errors so a broken chunk load
   or a runtime error on startup never leaves the user staring at a white
   screen. Shows a minimal branded recovery card with a "Ricarica" button
   that hard-clears service worker caches before reload — the exact fix
   for the PWA cold-start bug where a stale service worker served a broken
   cached bundle.
*/
import React from "react";

export default class AppErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // Best-effort telemetry — swallow if it fails so we never re-throw.
    try {
      console.error("[ORIO] fatal error", error);
    } catch { /* noop */ }
  }

  hardReload = async () => {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch { /* proceed */ }
    // Add a cache-busting query param so the browser fetches a fresh index.html.
    window.location.href = "/?r=" + Date.now();
  };

  render() {
    if (this.state.error) {
      return (
        <div
          data-testid="app-error-boundary"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "linear-gradient(180deg, #FBF7F1 0%, #F4EFE6 100%)",
            color: "#1A1A1A",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          <div style={{ textAlign: "center", maxWidth: 380 }}>
            <img
              src="/brand/orio-symbol.svg"
              alt="ORIO"
              style={{ height: 48, marginBottom: 20, opacity: 0.85 }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
              Oops, ORIO ha bisogno di un istante
            </div>
            <div style={{ fontSize: 14, color: "#6B6B6B", lineHeight: 1.5, marginBottom: 24 }}>
              Sto ripulendo la cache e ricarico l'app. Riprova tra un secondo — i tuoi dati sono al sicuro.
            </div>
            <button
              onClick={this.hardReload}
              data-testid="error-reload"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                color: "#FFFFFF",
                border: 0,
                borderRadius: 999,
                padding: "12px 24px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 10px 22px -8px rgba(255,122,26,0.5)",
              }}
            >
              Ricarica ORIO
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
